import { AGENT_DEFAULT_NAME } from '@/pages/agents/constants';
import { getAgentLanguage, useGetCurrentAgentsPresenter } from '@/pages/agents/hooks';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { useAgentEditorStore } from '@/state/root';
import { debounce } from '@/utils/utils';
import { VideoType } from '@d-id/types';
import { toJS } from 'mobx';
import { useEffect, useMemo } from 'react';
import { useSaveDraftAgent } from '../api';
import { getAgentBgObject } from '../utils';

export const useAutosaveDraftAgent = () => {
    const agentEditorStore = useAgentEditorStore();

    const { mutateAsync: saveDraftAgent } = useSaveDraftAgent();
    const { isAnonymous } = useAuth();
    const currentPresenter = useGetCurrentAgentsPresenter();

    const debouncedSave = useMemo(() => {
        return debounce((draft: any) => {
            saveDraftAgent({
                agentId: draft?.id,
                presenter: draft?.presenter,
                agentName: draft?.agentName,
                voiceProvider: draft?.voice,
                greetings: draft?.greetings,
                presenterIsStitch: draft?.presenterIsStitch,
                llm: draft?.llm,
                triggers: draft?.triggers,
            })
                .then(() => {
                    try {
                        if (!isAnonymous) {
                            window.DID_AGENTS_API.updateAgent();
                        }
                    } catch (e) {
                        logger.error('Failed to update agent from playground', e);
                    }
                })
                .catch(logger.error);
        });
    }, [saveDraftAgent, isAnonymous]);

    useEffect(() => {
        const {
            ttsSelectedVoice,
            selectedPresenter,
            inputAgent: { id, preview_name },
            greetings,
            presenterIsStitch,
            starterQuestions,
            presenterBackgroundSourceUrl,
            presenterBackgroundColor,
            presenterBackgroundThumbnailUrl,
            llm,
            ttsSelectedLanguage,
            triggers,
        } = agentEditorStore;

        const voiceProvider = ttsSelectedVoice
            ? {
                  type: ttsSelectedVoice.provider,
                  voice_id: ttsSelectedVoice.id,
                  access: ttsSelectedVoice.access,
                  language: getAgentLanguage(ttsSelectedVoice, ttsSelectedLanguage),
              }
            : null;

        const updatedPresenter = currentPresenter && {
            ...currentPresenter,
            ...(currentPresenter?.type === VideoType.CLIP &&
                getAgentBgObject(currentPresenter.type, presenterBackgroundColor, presenterBackgroundSourceUrl)),
            ...(currentPresenter?.type === VideoType.TALK && {
                v1Poster: presenterBackgroundSourceUrl || currentPresenter?.poster,
                thumbnail: presenterBackgroundThumbnailUrl || currentPresenter?.thumbnail || currentPresenter?.poster,
            }),
        };

        debouncedSave(1000, {
            id,
            presenterId: selectedPresenter,
            presenter: updatedPresenter,
            agentName: preview_name || AGENT_DEFAULT_NAME,
            language: ttsSelectedVoice?.language,
            voiceId: ttsSelectedVoice?.id,
            voice: voiceProvider,
            greetings: toJS(greetings),
            presenterIsStitch,
            starterMessage: toJS(starterQuestions),
            llm: toJS(llm),
            triggers: toJS(triggers),
        });
    }, [
        currentPresenter,
        debouncedSave,
        agentEditorStore.ttsSelectedVoice,
        agentEditorStore.presenterIsStitch,
        agentEditorStore.selectedPresenter,
        agentEditorStore.greetings,
        agentEditorStore.triggers,
        agentEditorStore.inputAgent.id,
        agentEditorStore.inputAgent.preview_name,
        agentEditorStore.ttsSelectedLanguage,
        agentEditorStore.presenterBackgroundSourceUrl,
        agentEditorStore.presenterBackgroundColor,
        agentEditorStore.presenterBackgroundThumbnailUrl,
        agentEditorStore.llm.template,
        agentEditorStore.llm.temperature,
        agentEditorStore.llm.instructions,
        agentEditorStore.llm.prompt_customization?.max_response_length,
        agentEditorStore.llm.prompt_customization?.personality,
        agentEditorStore.llm.prompt_customization?.role,
        agentEditorStore.llm.prompt_customization?.topics_to_avoid,
        agentEditorStore.llm.prompt_customization?.knowledge_source,
        agentEditorStore.starterQuestions,
    ]);
};
